import { useContext, useEffect, useState } from 'react';
import { addDays, differenceInCalendarDays, format, isSameDay } from 'date-fns';
import useAppQuery from 'hooks/useAppQuery';
import useAppUser from 'hooks/useAppUser';
import { PropertyBusinessType } from 'models/Properties';
import { GetPropertiesEntriesResponseTO } from 'models/PropertiesEntries';
import StandaloneCalendarContext from 'pages/calendar/CalendarContext';
import { CalendarDay } from 'pages/calendar/body/CalendarBody.types';
import CalendarBodyContext from 'pages/calendar/body/CalendarBodyContext';
import CalendarContext from 'pages/stackedCalendar/CalendarContext';
import useGetCalendarDataProcess from 'pages/stackedCalendar/useGetCalendarDataProcess';
import API from 'services/API';
import { getUnitType } from 'utils/lead/leadUtils';
import useCalendarBodyRefreshRequest from '../stackedCalendar/useCalendarBodyRefreshRequest';
import { StandaloneCalendarDataFetchParams } from './Calendar.types';
import { canUsePropertyCalendarCache } from './Calendar.utils';

const usePropertyCalendarsData = () => {
  const { daysCalendar, setDaysCalendar } = useContext(CalendarBodyContext);
  const { calendarFilter } = useContext(StandaloneCalendarContext);
  const { selectedProperty, propertiesMap, allProperties } =
    useContext(CalendarContext);
  const bodyRefreshRequest = useCalendarBodyRefreshRequest();
  const { agencyUid } = useAppUser();
  const processFetchedData = useGetCalendarDataProcess();
  const [fetchParams, setFetchParams] =
    useState<StandaloneCalendarDataFetchParams>();

  const {
    data: propertyCalendars,
    isInitialLoading: isPropertyCalendarsLoading,
  } = useAppQuery(
    ['getPropertyCalendars', selectedProperty, fetchParams, agencyUid],
    async () => {
      // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      const { start, end } = fetchParams;

      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      const propertyToFetch = getUnitType(allProperties, selectedProperty);

      const from = format(start, 'yyyy-MM-dd');
      const to = format(end, 'yyyy-MM-dd');
      const response = await API.get<GetPropertiesEntriesResponseTO>(
        `/v3/property-calendar?propertiesUids=${
          propertyToFetch?.uid || selectedProperty
        }&from=${from}&to=${to}&availabilityComments=true`,
      );
      return response.data.propertyCalendars;
    },
    {
      enabled:
        !!fetchParams &&
        !canUsePropertyCalendarCache(
          // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          daysCalendar[selectedProperty]?.daysCalendar,
          // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          calendarFilter,
        ),
    },
  );

  useEffect(() => {
    // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    if (!propertyCalendars && propertiesMap[selectedProperty]) {
      // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      const property = propertiesMap[selectedProperty];

      if (property.businessType === PropertyBusinessType.UNIT) {
        // @ts-expect-error TS2339 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        const { start, end } = calendarFilter;

        // @ts-expect-error TS7034 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        const days = [];
        for (
          let tmp = start;
          differenceInCalendarDays(tmp, end) <= 0;
          tmp = addDays(tmp, 1)
        ) {
          // @ts-expect-error TS2352 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          const obj: CalendarDay = {
            availableForCheckIn: true,
            date: tmp,
            isLoading: false,
            minimumStay: 0,
            price: null,
            unavailable: false,
            jobs: null,
            leads: null,
          } as CalendarDay;

          days.push(obj);
        }
        setDaysCalendar((prevState) => ({
          ...prevState,
          // @ts-expect-error TS2464 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          [selectedProperty]: {
            // @ts-expect-error TS7005 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            daysCalendar: days,
          },
        }));
      }
      return;
    }

    if (!propertyCalendars) return;

    propertyCalendars.forEach(({ entries, propertyUid }) => {
      const property = propertiesMap[propertyUid];
      const shouldActAsUnitType =
        property?.businessType === PropertyBusinessType.UNIT_TYPE &&
        // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        propertiesMap[selectedProperty]?.businessType ===
          PropertyBusinessType.UNIT;

      for (let e = 0; e < entries.length; e += 1) {
        const entry = entries[e];
        daysCalendar[
          shouldActAsUnitType ? selectedProperty! : propertyUid
        ]?.daysCalendar.map((dayCalendar) => {
          if (format(dayCalendar.date, 'yyyy-MM-dd') === entry.date) {
            dayCalendar.minimumStay = entry.availability?.minimumStayLength;
            dayCalendar.notes = entry.availability?.notes;
            dayCalendar.unavailable = entry.availability?.unavailable;
            dayCalendar.availableForCheckIn =
              entry.availability?.availableForCheckIn;
            dayCalendar.availableForCheckout =
              entry.availability?.availableForCheckOut;
            dayCalendar.price = {
              value: entry.pricing?.value,
              currency: entry.pricing?.currency,
            };
            dayCalendar.isLoading = false;
            dayCalendar.unbookableNight = entry.availability?.unbookableNight;
          }
          return dayCalendar;
        });
      }
    });

    processFetchedData([propertyCalendars, [], [], []]);

    setDaysCalendar((prevState) => ({
      ...prevState,
      // @ts-expect-error TS2464 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      [selectedProperty]: {
        // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        daysCalendar: daysCalendar[selectedProperty]?.daysCalendar,
      },
    }));
  }, [propertyCalendars]);

  useEffect(() => {
    if (isPropertyCalendarsLoading) {
      setDaysCalendar((prevState) => {
        // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        let days = prevState[selectedProperty]?.daysCalendar;
        if (!days) {
          days = [];
        }

        for (
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          let tmp = calendarFilter.start;
          // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          differenceInCalendarDays(tmp, calendarFilter.end) <= 0;
          tmp = addDays(tmp, 1)
        ) {
          // @ts-expect-error TS2352 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          const obj: CalendarDay = {
            availableForCheckIn: true,
            date: tmp,
            isLoading: true,
            minimumStay: 0,
            price: null,
            unavailable: false,
            jobs: null,
            leads: null,
          } as CalendarDay;

          // @ts-expect-error TS7006 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          if (!days.find((day) => isSameDay(day.date, tmp))) {
            days.push(obj);
          }
        }

        return {
          ...prevState,
          // @ts-expect-error TS2464 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
          [selectedProperty]: {
            daysCalendar: days,
          },
        };
      });
    }
  }, [isPropertyCalendarsLoading, fetchParams]);

  useEffect(() => {
    setFetchParams({
      // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      start: calendarFilter.start,
      // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      end: calendarFilter.end,
    });
    // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  }, [calendarFilter.start, calendarFilter.end]);

  useEffect(() => {
    if (bodyRefreshRequest) {
      const { properties } = bodyRefreshRequest;

      const unitTypeFromUnit =
        // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        propertiesMap[selectedProperty].unitTypeUid === properties[0];
      // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      if (!properties.includes(selectedProperty) && !unitTypeFromUnit) {
        return;
      }

      setFetchParams({
        // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        start: calendarFilter.start,
        // @ts-expect-error TS18048 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
        end: calendarFilter.end,
        requestId: new Date().getTime(),
      });
    }
  }, [bodyRefreshRequest]);

  return {
    isLoading:
      isPropertyCalendarsLoading &&
      // @ts-expect-error TS2538 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
      !daysCalendar[selectedProperty]?.daysCalendar.length,
  };
};

export default usePropertyCalendarsData;
